import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlus, PencilSimple, Trash, Users, Gear, ShieldCheck, User, Robot, Copy, ArrowsClockwise } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { CONTENT_PAGES } from "@/lib/permissions";

const emptyForm = { email: "", password: "", name: "", role: "staff" };
const ROLES = [
  { value: "admin", label: "Admin", desc: "Full access incl. pricing & user management" },
  { value: "staff", label: "Staff", desc: "Read & write catalogue/stock (no pricing)" },
  { value: "viewer", label: "Viewer", desc: "Sees only the Stock on Hand tab — availability status, no pricing, no exact numbers" },
];

export default function SettingsPage() {
  const { user: me, isAdmin, refresh } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "staff", password: "" });
  const [staffPages, setStaffPages] = useState([]);
  const [permLoading, setPermLoading] = useState(true);
  const [savingPerm, setSavingPerm] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyLoaded, setApiKeyLoaded] = useState(false);
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const invUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/inventory`;

  const load = async () => {
    if (!isAdmin) { setLoading(false); return; }
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) { setPermLoading(false); return; }
    api.get("/permissions")
      .then(({ data }) => setStaffPages(data.staff_pages))
      .catch(() => {})
      .finally(() => setPermLoading(false));
  }, [isAdmin]);

  const togglePage = async (key, val) => {
    let next = val ? Array.from(new Set([...staffPages, key])) : staffPages.filter((k) => k !== key);
    if (!next.includes("stock-on-hand")) next = [...next, "stock-on-hand"];
    setStaffPages(next);
    setSavingPerm(true);
    try {
      const { data } = await api.put("/permissions", { pages: next });
      setStaffPages(data.staff_pages);
      toast.success("Staff tab access updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSavingPerm(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/settings/bot-api-key")
      .then(({ data }) => setApiKey(data.api_key))
      .catch(() => {})
      .finally(() => setApiKeyLoaded(true));
  }, [isAdmin]);

  const regenerateKey = async () => {
    setApiKeyBusy(true);
    try {
      const { data } = await api.post("/settings/bot-api-key/regenerate");
      setApiKey(data.api_key);
      setShowKey(true);
      toast.success("New API key generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setApiKeyBusy(false); }
  };
  const disableKey = async () => {
    setApiKeyBusy(true);
    try {
      await api.delete("/settings/bot-api-key");
      setApiKey(null);
      toast.success("API key disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setApiKeyBusy(false); }
  };
  const copyKey = () => { navigator.clipboard?.writeText(apiKey); toast.success("Copied to clipboard"); };

  const onCreate = async () => {
    try {
      await api.post("/auth/register", createForm);
      toast.success("User created");
      setCreateOpen(false);
      setCreateForm(emptyForm);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditForm({ name: u.name, role: u.role, password: "" });
  };

  const onUpdate = async () => {
    try {
      const payload = { name: editForm.name, role: editForm.role };
      if (editForm.password) payload.password = editForm.password;
      await api.put(`/users/${editing.id}`, payload);
      toast.success("User updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const onDelete = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  // Self-service password change (any role)
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  // Self-service email change (any role)
  const [emailCurrentPw, setEmailCurrentPw] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [changingEmail, setChangingEmail] = useState(false);

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== newPw2) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setChangingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Password updated");
      setCurrentPw(""); setNewPw(""); setNewPw2("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setChangingPw(false); }
  };

  const onChangeEmail = async (e) => {
    e.preventDefault();
    setChangingEmail(true);
    try {
      await api.post("/auth/change-email", {
        current_password: emailCurrentPw,
        new_email: newEmail.trim().toLowerCase(),
      });
      toast.success(`Email updated to ${newEmail.trim().toLowerCase()}. Sign in with the new email next time.`);
      setEmailCurrentPw(""); setNewEmail("");
      // Refresh auth context to reflect new email in sidebar
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setChangingEmail(false); }
  };

  const myAccountCard = (
    <section className="mb-10">
      <h2 className="font-heading text-xl font-semibold flex items-center gap-2 mb-1">
        <User size={20} /> My Account
      </h2>
      <p className="text-xs text-slate-500 mb-4">Signed in as <span className="font-mono">{me.email}</span> · {me.role}</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-3xl">
        <form onSubmit={onChangePassword} className="border border-slate-200 bg-white p-5" data-testid="change-pw-form">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-3">Change Password</div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Current password</Label>
              <Input type="password" required value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                data-testid="current-pw-input" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">New password</Label>
              <Input type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)}
                data-testid="new-pw-input" className="font-mono" placeholder="At least 6 characters" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Confirm new password</Label>
              <Input type="password" required value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                data-testid="confirm-pw-input" className="font-mono" />
            </div>
            <Button type="submit" disabled={changingPw || !currentPw || !newPw || !newPw2}
              className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2 w-full" data-testid="change-pw-submit">
              {changingPw ? "Updating…" : "Update password"}
            </Button>
          </div>
        </form>

        <form onSubmit={onChangeEmail} className="border border-slate-200 bg-white p-5" data-testid="change-email-form">
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-3">Change Email</div>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Current password</Label>
              <Input type="password" required value={emailCurrentPw} onChange={(e) => setEmailCurrentPw(e.target.value)}
                data-testid="email-current-pw-input" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">New email</Label>
              <Input type="email" required value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                data-testid="new-email-input" className="font-mono"
                placeholder="admin@vinylwrappingaustralia.com" />
              <div className="text-[10px] text-slate-500">You&apos;ll sign in with this email next time.</div>
            </div>
            <Button type="submit" disabled={changingEmail || !emailCurrentPw || !newEmail}
              className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2 w-full" data-testid="change-email-submit">
              {changingEmail ? "Updating…" : "Update email"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-[1100px]">
        <header className="mb-8">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Workspace</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Gear size={32} /> Settings
          </h1>
        </header>
        {myAccountCard}
        <div className="border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600 max-w-md">
          <ShieldCheck size={20} className="text-slate-400 mb-2" />
          User management is admin-only. Contact your workspace admin to invite or update other accounts.
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1100px]">
      <header className="mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Workspace · Configuration</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
          <Gear size={32} /> Settings
        </h1>
        <p className="text-sm text-slate-600 mt-2">Manage user access for your workspace.</p>
      </header>

      {myAccountCard}

      <section className="mb-10">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Users size={20} /> Team members</h2>
            <p className="text-xs text-slate-500 mt-1">Admins can edit pricing and manage users. Staff can manage catalogue and stock.</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="invite-user-btn">
                <UserPlus size={16} /> Add user
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-2">
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Name</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="new-user-name" />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Email</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="new-user-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Password</Label>
                  <Input type="text" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} className="font-mono" data-testid="new-user-password" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Role</Label>
                  <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    {ROLES.find((r) => r.value === createForm.role)?.desc}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={onCreate}
                  disabled={!createForm.email || !createForm.password || createForm.password.length < 6 || !createForm.name}
                  className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="new-user-save">Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3 w-32"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium flex items-center gap-2">
                      <div className="w-7 h-7 grid place-items-center bg-slate-900 text-white rounded-full text-[10px] font-mono">
                        {u.name?.[0]?.toUpperCase() || <User size={12} />}
                      </div>
                      {u.name}
                      {u.id === me.id && <span className="text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-sm">You</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border ${
                        u.role === "admin" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                        u.role === "viewer" ? "bg-purple-50 text-purple-700 border-purple-200" :
                        "bg-slate-100 text-slate-600 border-slate-200"
                      }`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" disabled={u.id === me.id}
                              className="text-red-600 disabled:text-slate-300" data-testid={`delete-user-${u.id}`}>
                              <Trash size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>{u.email} will lose access immediately.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="rounded-md">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDelete(u)} className="bg-red-600 hover:bg-red-700 rounded-md">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Staff tab access */}
      <section className="mb-10">
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><ShieldCheck size={20} /> Staff Tab Access</h2>
          <p className="text-xs text-slate-500 mt-1">Choose which tabs <b>Staff</b> accounts can open. Admins always see everything; Viewers only ever see Stock on Hand.</p>
        </div>
        {permLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="border border-slate-200 bg-white divide-y divide-slate-100 max-w-2xl" data-testid="staff-permissions">
            {CONTENT_PAGES.map((pg) => {
              const enabled = staffPages.includes(pg.key);
              const locked = pg.key === "stock-on-hand" || pg.key === "per-unit-products" || pg.key === "scan";
              return (
                <div key={pg.key} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <div className="text-sm font-medium text-slate-800">{pg.label}</div>
                    {locked && <div className="text-[10px] text-slate-400">Always enabled</div>}
                  </div>
                  <Switch
                    checked={enabled}
                    disabled={locked || savingPerm}
                    onCheckedChange={(v) => togglePage(pg.key, v)}
                    data-testid={`perm-toggle-${pg.key}`}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Bot Inventory API */}
      <section className="mb-10" data-testid="bot-api-section">
        <div className="mb-4">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><Robot size={20} /> Bot Inventory API</h2>
          <p className="text-xs text-slate-500 mt-1">A read-only key so an external bot (e.g. your Claude assistant) can fetch live inventory counts. Stock only — no pricing. Treat the key like a password.</p>
        </div>
        <div className="border border-slate-200 bg-white p-5 max-w-2xl space-y-4">
          <div>
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-1.5">API key</div>
            {!apiKeyLoaded ? (
              <div className="text-sm text-slate-400">Loading…</div>
            ) : apiKey ? (
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-xs" data-testid="bot-api-key-value">
                  {showKey ? apiKey : "•".repeat(20) + apiKey.slice(-4)}
                </code>
                <Button size="sm" variant="outline" onClick={() => setShowKey((s) => !s)} className="rounded-md" data-testid="bot-api-toggle">{showKey ? "Hide" : "Show"}</Button>
                <Button size="sm" variant="outline" onClick={copyKey} className="rounded-md gap-1.5" data-testid="bot-api-copy"><Copy size={14} /> Copy</Button>
              </div>
            ) : (
              <div className="text-sm text-slate-500">No key yet — generate one to enable the API.</div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={regenerateKey} disabled={apiKeyBusy} className="bg-blue-600 hover:bg-blue-700 rounded-md gap-2" data-testid="bot-api-regenerate">
              <ArrowsClockwise size={15} /> {apiKey ? "Regenerate key" : "Generate key"}
            </Button>
            {apiKey && (
              <Button onClick={disableKey} disabled={apiKeyBusy} variant="outline" className="rounded-md text-red-600 border-red-200 hover:bg-red-50" data-testid="bot-api-disable">Disable</Button>
            )}
          </div>
          {apiKey && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">Regenerating immediately invalidates the old key — update your bot afterwards.</div>
          )}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-1.5">Endpoint</div>
            <code className="block bg-slate-900 text-slate-100 rounded-md px-3 py-2 font-mono text-[11px] overflow-x-auto" data-testid="bot-api-endpoint">GET {invUrl}</code>
            <div className="text-[11px] text-slate-500 mt-2">Send your key in the <span className="font-mono">X-API-Key</span> header. Add <span className="font-mono">?include_rolls=true</span> for a per-roll breakdown.</div>
            <code className="block bg-slate-50 border border-slate-200 rounded-md px-3 py-2 font-mono text-[11px] overflow-x-auto mt-1.5">curl -H "X-API-Key: YOUR_KEY" {invUrl}</code>
          </div>
        </div>
      </section>

      {/* Edit user dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5 col-span-2">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">New password (optional)</Label>
              <Input type="text" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="Leave blank to keep" className="font-mono" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="rounded-md">Cancel</Button>
            <Button onClick={onUpdate} className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="edit-user-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Workspace info */}
      <section>
        <h2 className="font-heading text-xl font-semibold mb-3">Workspace</h2>
        <div className="border border-slate-200 bg-white">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-200">
            <div className="p-5">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Signed in as</div>
              <div className="font-mono text-sm mt-1">{me.email}</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Your role</div>
              <div className="font-mono text-sm mt-1 capitalize">{me.role}</div>
            </div>
            <div className="p-5">
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Total users</div>
              <div className="font-mono text-sm mt-1">{users.length}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
